/**
 * Self-contained UI the tracker injects into the host page:
 *   - showConsent()      — a corner consent card (askPermission)
 *   - showRecordingDot() — a minimal pulsing "recording" dot (showRecording)
 *
 * Isolation rules (the SDK is otherwise headless):
 *   - attachShadow({mode:'open'}) + adoptedStyleSheets → no host CSS bleed and
 *     CSP-safe (no inline <style>, so `style-src 'self'` pages don't block it).
 *   - the host element wears the recorder's block class (default `.logclip-block`)
 *     so the SDK's own rrweb recorder never captures its own UI. The host box is
 *     zero-size (its children are position:fixed), so no placeholder shows in the
 *     replay.
 */

export interface ConsentText {
  title?: string;
  body?: string;
  allow?: string;
  deny?: string;
}

/** Derive a class the host can wear so the recorder's blockSelector excludes it.
 *  blockSelector is usually a class selector (default '.logclip-block'). We always
 *  keep both default classes too, so a custom blockSelector can't accidentally
 *  un-hide. */
function blockClassFrom(blockSelector: string): string {
  const m = /\.([A-Za-z0-9_-]+)/.exec(blockSelector || '');
  const derived = m && m[1] ? m[1] : '';
  return ['logclip-block', 'obsrviq-block', derived].filter(Boolean).join(' ');
}

function makeHost(blockSelector: string): { host: HTMLElement; root: ShadowRoot } {
  const host = document.createElement('div');
  host.className = blockClassFrom(blockSelector);
  host.setAttribute('data-obsrviq-ui', '');
  host.style.cssText = 'all:initial';
  const root = host.attachShadow({ mode: 'open' });
  return { host, root };
}

function adopt(root: ShadowRoot, css: string): void {
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    root.adoptedStyleSheets = [sheet];
  } catch {
    // Engines without constructable stylesheets — inline <style> inside the shadow
    // (still isolated; only a concern on host pages with a strict style-src CSP).
    const style = document.createElement('style');
    style.textContent = css;
    root.appendChild(style);
  }
}

function mount(host: HTMLElement): void {
  (document.body || document.documentElement).appendChild(host);
}

/* LogClip blue. This UI (consent card, REC dot) renders on the CUSTOMER's site
 * inside a shadow root, so it can't read our CSS custom properties — the hex has
 * to be kept in step with --lc-brand by hand. */
const ACCENT = '#5b89df';
/** ACCENT at 20%, for the halo behind the dot. Kept beside it so the two can
 *  never drift apart again — the halo was left amber by the rebrand. */
const ACCENT_HALO = 'rgba(91,137,223,.20)';

const CONSENT_CSS = `
:host { all: initial; }
.card {
  position: fixed; bottom: 20px; right: 20px; z-index: 2147483647;
  max-width: 340px; box-sizing: border-box;
  display: grid; grid-template-columns: auto 1fr;
  grid-template-areas: 'dot txt' 'actions actions'; gap: 9px 12px; padding: 16px 18px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: #f4f5fb; background: #14151b; border: 1px solid rgba(255,255,255,.08);
  border-radius: 14px; box-shadow: 0 16px 48px rgba(0,0,0,.5);
  opacity: 0; transform: translateY(10px); transition: opacity .22s ease, transform .22s ease;
}
.card.in { opacity: 1; transform: none; }
.dot { grid-area: dot; width: 9px; height: 9px; margin-top: 5px; border-radius: 50%;
  background: ${ACCENT}; box-shadow: 0 0 0 4px ${ACCENT_HALO}; }
.txt { grid-area: txt; min-width: 0; }
.title { font-size: 13.5px; font-weight: 600; line-height: 1.35; }
.body { margin-top: 3px; font-size: 12px; line-height: 1.45; color: #a9adbe; }
.actions { grid-area: actions; display: flex; gap: 8px; justify-content: flex-end; margin-top: 2px; }
button { font: inherit; font-size: 12.5px; font-weight: 600; padding: 7px 15px; border-radius: 9px;
  cursor: pointer; border: 1px solid transparent; transition: background .15s ease, color .15s ease; }
.deny { background: transparent; color: #a9adbe; border-color: rgba(255,255,255,.14); }
.deny:hover { color: #f4f5fb; background: rgba(255,255,255,.05); }
.allow { background: ${ACCENT}; color: #fff; }
.allow:hover { background: #5a62e0; }
button:focus-visible { outline: 2px solid ${ACCENT}; outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) { .card { transition: none; } }
@media (max-width: 480px) { .card { left: 16px; right: 16px; bottom: 16px; max-width: none; } }
`;

const DOT_CSS = `
:host { all: initial; }
.recdot {
  position: fixed; bottom: 16px; right: 16px; z-index: 2147483647;
  width: 12px; height: 12px; border-radius: 50%;
  background: #ef4444; box-shadow: 0 0 0 0 rgba(239,68,68,.5);
  animation: lum-pulse 2s ease-out infinite;
}
@keyframes lum-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(239,68,68,.5); }
  70%  { box-shadow: 0 0 0 9px rgba(239,68,68,0); }
  100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
}
@media (prefers-reduced-motion: reduce) { .recdot { animation: none; } }
`;

/** Corner consent card. Resolves via onAllow/onDeny exactly once, then unmounts.
 *  Returns an unmount fn (e.g. to tear down if consent is set programmatically). */
export function showConsent(
  blockSelector: string,
  text: ConsentText | undefined,
  onAllow: () => void,
  onDeny: () => void,
): () => void {
  const { host, root } = makeHost(blockSelector);
  adopt(root, CONSENT_CSS);
  const t = text ?? {};

  const card = document.createElement('div');
  card.className = 'card';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-label', t.title || 'Session recording consent');

  const dot = document.createElement('div');
  dot.className = 'dot';
  dot.setAttribute('aria-hidden', 'true');

  const txt = document.createElement('div');
  txt.className = 'txt';
  const title = document.createElement('div');
  title.className = 'title';
  title.textContent = t.title || 'We record this session to improve the experience.';
  const body = document.createElement('div');
  body.className = 'body';
  body.textContent = t.body || 'No passwords or typed input are captured.';
  txt.append(title, body);

  const actions = document.createElement('div');
  actions.className = 'actions';
  const deny = document.createElement('button');
  deny.type = 'button';
  deny.className = 'deny';
  deny.textContent = t.deny || 'Decline';
  const allow = document.createElement('button');
  allow.type = 'button';
  allow.className = 'allow';
  allow.textContent = t.allow || 'Allow';
  actions.append(deny, allow);

  card.append(dot, txt, actions);
  root.appendChild(card);
  mount(host);
  requestAnimationFrame(() => card.classList.add('in'));

  let done = false;
  const unmount = () => {
    if (host.parentNode) host.parentNode.removeChild(host);
  };
  const choose = (fn: () => void) => () => {
    if (done) return;
    done = true;
    unmount();
    fn();
  };
  allow.addEventListener('click', choose(onAllow));
  deny.addEventListener('click', choose(onDeny));
  return unmount;
}

/** Minimal pulsing "recording" dot (bottom-right). Returns an unmount fn. */
export function showRecordingDot(blockSelector: string): () => void {
  const { host, root } = makeHost(blockSelector);
  adopt(root, DOT_CSS);
  const dot = document.createElement('div');
  dot.className = 'recdot';
  dot.setAttribute('role', 'status');
  dot.setAttribute('aria-label', 'Session recording in progress');
  root.appendChild(dot);
  mount(host);
  return () => {
    if (host.parentNode) host.parentNode.removeChild(host);
  };
}
